import type {
  AiStreamFrame,
  AiUiResponse,
  AccountType,
  AccountVisibility,
  Cadence,
  CategoryKind,
  ForecastMonth,
  LoanStatus,
  TimelineResponse,
  TransactionStatus,
  TransactionType,
} from "@vouch/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiFetch } from "./api";

export interface Period {
  month?: number;
  year?: number;
}

function periodQuery(p?: Period): string {
  const params = new URLSearchParams();
  if (p?.month) params.set("month", String(p.month));
  if (p?.year) params.set("year", String(p.year));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export interface Account {
  id: string;
  userId: string;
  name: string;
  type: AccountType;
  currency: string;
  cachedBalance: number;
  visibility: AccountVisibility;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  currency: string;
  description: string | null;
  status: TransactionStatus;
  occurredAt: string;
  createdByUserId: string;
  senderUserId: string | null;
  receiverUserId: string | null;
  senderAccountId: string | null;
  receiverAccountId: string | null;
  recurringRuleId: string | null;
  senderAccountName: string | null;
  receiverAccountName: string | null;
  senderUserName: string | null;
  receiverUserName: string | null;
  categoryName: string | null;
  createdAt: string;
}

export interface Category {
  id: string;
  userId: string | null;
  kind: CategoryKind;
  name: string;
  icon: string | null;
  color: string | null;
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => api<{ categories: Category[] }>("/categories").then((r) => r.categories),
    staleTime: 5 * 60 * 1000,
  });
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

export function useNotifications(period?: Period) {
  return useQuery({
    queryKey: ["notifications", period?.month ?? null, period?.year ?? null],
    queryFn: () =>
      api<{ notifications: Notification[] }>(`/notifications${periodQuery(period)}`).then(
        (r) => r.notifications,
      ),
    // Poll as a fallback so a new notification shows within ~5s even if the
    // live WebSocket drops; the WS still delivers instantly when connected.
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });
}

/** Unread count for the nav badge (shares the notifications cache, polls 5s). */
export function useUnreadCount(): number {
  const { data } = useNotifications();
  return (data ?? []).filter((n) => !n.readAt).length;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/notifications/read-all", { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export interface AccountHistory {
  points: Array<Record<string, number | string>>;
  accounts: string[];
}

export function useAccountHistory(months = 6) {
  return useQuery({
    queryKey: ["account-history", months],
    queryFn: () => api<AccountHistory>(`/accounts/history?months=${months}`),
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: () => api<{ accounts: Account[] }>("/accounts").then((r) => r.accounts),
  });
}

export function useTransactions(period?: Period, accountId?: string) {
  return useQuery({
    queryKey: ["transactions", period?.month ?? null, period?.year ?? null, accountId ?? null],
    queryFn: () => {
      const params = new URLSearchParams();
      if (period?.month) params.set("month", String(period.month));
      if (period?.year) params.set("year", String(period.year));
      if (accountId) params.set("accountId", accountId);
      const qs = params.toString();
      return api<{ transactions: Transaction[] }>(`/transactions${qs ? `?${qs}` : ""}`).then(
        (r) => r.transactions,
      );
    },
  });
}

/** Recent transactions for a single account — used by the expandable account cards. */
export function useAccountTransactions(accountId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["account-transactions", accountId],
    enabled,
    queryFn: () =>
      api<{ transactions: Transaction[] }>(`/transactions?accountId=${accountId}&limit=5`).then(
        (r) => r.transactions,
      ),
  });
}

export function useConfirmTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; accountId?: string }) =>
      api(`/transactions/${vars.id}/confirm`, {
        method: "POST",
        body: { accountId: vars.accountId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
      void qc.invalidateQueries({ queryKey: ["recurring-due"] });
    },
  });
}

export function useSkipTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/transactions/${id}/skip`, { method: "POST", body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["transactions"] });
      void qc.invalidateQueries({ queryKey: ["recurring-due"] });
    },
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      type: AccountType;
      currency: string;
      openingBalanceMinor: number;
      visibility: AccountVisibility;
      description?: string;
    }) => api("/accounts", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name?: string; type?: string; description?: string }) =>
      api(`/accounts/${vars.id}`, {
        method: "PATCH",
        body: { name: vars.name, type: vars.type, description: vars.description },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useUpdateAccountVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; visibility: AccountVisibility }) =>
      api(`/accounts/${vars.id}/visibility`, {
        method: "PATCH",
        body: { visibility: vars.visibility },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      type: "income" | "expense";
      accountId: string;
      amountMinor: number;
      currency: string;
      categoryId?: string;
      description?: string;
    }) => api("/transactions", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export interface Loan {
  id: string;
  lenderUserId: string;
  borrowerUserId: string;
  initiatorUserId: string;
  disburseAccountId: string | null;
  principal: number;
  outstanding: number;
  currency: string;
  startDate: string;
  dueDate: string | null;
  status: LoanStatus;
  requiresWitness: boolean;
  acceptedAt: string | null;
  rejectedAt: string | null;
  undoRequestedByUserId: string | null;
  undoAcceptedAt: string | null;
  description: string | null;
  lenderName: string | null;
  borrowerName: string | null;
  /** Set when a repayment is awaiting confirmation by one of the parties. */
  pendingRepayment: { id: string; amount: number; initiatorUserId: string } | null;
  pendingRepayments: Array<{
    id: string;
    amount: number;
    initiatorUserId: string;
    createdAt: string;
  }>;
}

export function useLoans(period?: Period) {
  return useQuery({
    queryKey: ["loans", period?.month ?? null, period?.year ?? null],
    queryFn: () => api<{ loans: Loan[] }>(`/loans${periodQuery(period)}`).then((r) => r.loans),
  });
}

export interface LoanRepaymentDetail {
  id: string;
  loanId: string;
  initiatorUserId: string;
  amount: number;
  paidAt: string;
  status: "pending" | "accepted" | "rejected" | "cancelled";
  payerAccountId: string | null;
  payerAccountName: string | null;
  receiveAccountId: string | null;
  receiveAccountName: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoanDetailResponse {
  loan: Loan & {
    lender: PublicPerson & { email: string };
    borrower: PublicPerson & { email: string };
    disburseAccountName: string | null;
    receiveAccountName: string | null;
  };
  repayments: LoanRepaymentDetail[];
  witnesses: Array<{
    id: string;
    status: "pending" | "confirmed" | "declined";
    respondedAt: string | null;
    createdAt: string;
    invitedEmail: string | null;
    person: PublicPerson | null;
  }>;
}

export function useLoan(id: string) {
  return useQuery({
    queryKey: ["loan", id],
    queryFn: () => api<LoanDetailResponse>(`/loans/${id}`),
  });
}

export function useTimeline(scope: "global" | "account" | "person" | "loan", id?: string) {
  const path = scope === "global" ? "/timeline?scope=global&limit=100" : `/${scope === "person" ? "people" : `${scope}s`}/${id}/timeline?limit=100`;
  return useQuery({
    queryKey: ["timeline", scope, id ?? null],
    enabled: scope === "global" || Boolean(id),
    queryFn: () => api<TimelineResponse>(path),
  });
}

export interface CreateLoanBody {
  direction: "lent" | "borrowed";
  counterpartyUsername?: string;
  counterpartyEmail?: string;
  counterpartyName?: string;
  accountId?: string;
  principalMinor: number;
  currency: string;
  dueDate?: string;
  description?: string;
  requiresWitness?: boolean;
  witnesses?: { email?: string; userId?: string; name?: string }[];
  fxRate?: number;
}

export function useFxRate(from: string, to: string) {
  return useQuery({
    queryKey: ["fx", from, to],
    enabled: Boolean(from && to && from !== to),
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      api<{ rate: number | null }>(`/fx/rate?from=${from}&to=${to}`).then((r) => r.rate),
  });
}

export function useFxRates(fromCurrencies: string[], to: string) {
  const currencies = [...new Set(fromCurrencies.map((c) => c.toUpperCase()).filter(Boolean))].sort();
  const target = to.toUpperCase();
  return useQuery({
    queryKey: ["fx-rates", target, currencies],
    enabled: Boolean(target),
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const entries = await Promise.all(
        currencies.map(async (from) => {
          if (from === target) return [from, 1] as const;
          const rate = await api<{ rate: number | null }>(`/fx/rate?from=${from}&to=${target}`).then(
            (r) => r.rate,
          );
          return [from, rate] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<string, number | null>;
    },
  });
}

export function useCreateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoanBody) =>
      api<{ loanId: string; status: string }>("/loans", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["loan"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export function useRespondLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; accept: boolean; accountId?: string }) =>
      api(`/loans/${vars.id}/respond`, {
        method: "POST",
        body: { accept: vars.accept, accountId: vars.accountId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["loan"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

/** Propose a repayment — the counterparty confirms before it posts. */
export function useRepayLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; accountId: string; amountMinor: number; paidAt?: string }) =>
      api<{ repaymentId: string; status: string }>(`/loans/${vars.id}/repay`, {
        method: "POST",
        body: { accountId: vars.accountId, amountMinor: vars.amountMinor, paidAt: vars.paidAt },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["loan"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

/** Withdraw a repayment while it is still awaiting confirmation. */
export function useCancelRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (repaymentId: string) =>
      api<{ status: "deleted" }>(`/loan-repayments/${repaymentId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["loan"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
    },
  });
}

/** Counterparty confirms/declines a pending repayment (names their own account). */
export function useRespondRepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { repaymentId: string; accept: boolean; accountId?: string }) =>
      api(`/loan-repayments/${vars.repaymentId}/respond`, {
        method: "POST",
        body: { accept: vars.accept, accountId: vars.accountId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["loan"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

/** Request to undo (reverse) a loan added by mistake. */
export function useRequestUndo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string }) =>
      api<{ status: string }>(`/loans/${vars.id}/undo`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

/** Counterparty approves/declines an undo request. */
export function useRespondUndo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; accept: boolean }) =>
      api(`/loans/${vars.id}/undo/respond`, { method: "POST", body: { accept: vars.accept } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export interface RecurringRule {
  id: string;
  kind: CategoryKind;
  amount: number;
  cadence: Cadence;
  description: string | null;
}

export function useRecurring() {
  return useQuery({
    queryKey: ["recurring"],
    queryFn: () => api<{ rules: RecurringRule[] }>("/recurring").then((r) => r.rules),
  });
}

export function useRecurringDue() {
  return useQuery({
    queryKey: ["recurring-due"],
    queryFn: () => api<{ transactions: Transaction[] }>("/recurring/due").then((r) => r.transactions),
  });
}

export function useCreateRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      kind: CategoryKind;
      amountMinor: number;
      cadence: Cadence;
      accountId?: string;
      categoryId?: string;
      dayOfMonth?: number;
      autoConfirm?: boolean;
      description?: string;
    }) => api("/recurring", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recurring"] });
      void qc.invalidateQueries({ queryKey: ["forecast"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useDeleteRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/recurring/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recurring"] });
      void qc.invalidateQueries({ queryKey: ["forecast"] });
    },
  });
}

export function useForecast(months: number) {
  return useQuery({
    queryKey: ["forecast", months],
    queryFn: () =>
      api<{ months: ForecastMonth[] }>(`/forecast?months=${months}`).then((r) => r.months),
  });
}

export interface Committee {
  id: string;
  name: string;
  organizerUserId: string;
  contributionAmount: number;
  currency: string;
  frequency: Cadence;
  totalSlots: number;
  payoutMode: "fixed" | "agreed" | "lottery";
  status: "forming" | "active" | "completed";
}

export interface CommitteeMember {
  id: string;
  userId: string;
  name: string;
  slotNumber: number | null;
  joinStatus: "invited" | "accepted" | "declined";
  payoutReceived: boolean;
}

export interface CommitteeRound {
  id: string;
  roundNumber: number;
  scheduledDate: string;
  payoutMemberId: string | null;
  status: "pending" | "collecting" | "paid";
  lotterySeed: string | null;
}

export interface CommitteeContribution {
  id: string;
  roundId: string;
  memberId: string;
  status: string;
}

export interface CommitteeDetail {
  committee: Committee;
  members: CommitteeMember[];
  rounds: CommitteeRound[];
  contributions: CommitteeContribution[];
}

export function useCommittees() {
  return useQuery({
    queryKey: ["committees"],
    queryFn: () => api<{ committees: Committee[] }>("/committees").then((r) => r.committees),
  });
}

export function useCommittee(id: string | null) {
  return useQuery({
    queryKey: ["committee", id],
    enabled: Boolean(id),
    queryFn: () => api<CommitteeDetail>(`/committees/${id}`),
  });
}

export function useCreateCommittee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      contributionAmountMinor: number;
      currency: string;
      totalSlots: number;
      payoutMode: "fixed" | "agreed" | "lottery";
      contributionAccountId?: string;
      payoutAccountId?: string;
    }) => api("/committees", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["committees"] }),
  });
}

function useCommitteeAction<T>(fn: (id: string, vars: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; vars: T }) => fn(args.id, args.vars),
    onSuccess: (_d, args) => {
      void qc.invalidateQueries({ queryKey: ["committee", args.id] });
      void qc.invalidateQueries({ queryKey: ["committees"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export const useInviteMember = () =>
  useCommitteeAction((id, vars: { username?: string; email?: string }) =>
    api(`/committees/${id}/invite`, { method: "POST", body: vars }),
  );

export const useRespondInvite = () =>
  useCommitteeAction(
    (id, vars: { accept: boolean; contributionAccountId?: string; payoutAccountId?: string }) =>
      api(`/committees/${id}/respond`, { method: "POST", body: vars }),
  );

export const useContribute = () =>
  useCommitteeAction((id, vars: { roundId: string; accountId?: string }) =>
    api(`/committees/${id}/rounds/${vars.roundId}/contribute`, {
      method: "POST",
      body: { accountId: vars.accountId },
    }),
  );

export const useStartCommittee = () =>
  useCommitteeAction((id, _vars: void) =>
    api(`/committees/${id}/start`, { method: "POST", body: {} }),
  );

export const useDrawRound = () =>
  useCommitteeAction((id, vars: { roundId: string; memberId?: string }) =>
    api(`/committees/${id}/rounds/${vars.roundId}/draw`, {
      method: "POST",
      body: { memberId: vars.memberId },
    }),
  );

export interface InvestSuggestion {
  instrument: string;
  amountMinor: number;
  riskLevel: "low" | "medium" | "high";
  rationale: string;
  horizonMonths?: number;
}

export interface InvestAdviceResult {
  advice: {
    safeToInvestMinor: number;
    suggestions: InvestSuggestion[];
    notes: string;
  };
  disclaimer: string;
}

export function useAiAvailable() {
  return useQuery({
    queryKey: ["ai-available"],
    queryFn: () => api<{ available: boolean }>("/ai/available").then((r) => r.available),
  });
}

export function useInvestSuggestions() {
  return useMutation({
    mutationFn: () => api<InvestAdviceResult>("/ai/invest-suggestions", { method: "POST", body: {} }),
  });
}

// ─── Finance Assistant (AI chat) ─────────────────────────────────────────────

export interface AssistantStep {
  sql: string;
  ok: boolean;
  rowCount?: number;
  error?: string;
}

export interface AssistantReply {
  conversationId: string;
  answer: string;
  steps: AssistantStep[];
  disclaimer: string;
}

export interface AssistantTurn {
  role: "user" | "assistant";
  content: string;
}

export function useAskAssistant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { message: string; history?: AssistantTurn[]; conversationId?: string }) =>
      api<AssistantReply>("/ai/assistant", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-conversations"] });
      // The persisted turn changes the conversation's stored history.
      void qc.invalidateQueries({ queryKey: ["ai-conversation"] });
    },
  });
}

export type { AiStreamFrame, AiUiResponse };

export async function streamAssistant(
  body: {
    message: string;
    history?: AssistantTurn[];
    conversationId?: string;
    mode?: "read" | "read_add" | "full";
  },
  onFrame: (frame: AiStreamFrame) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await apiFetch("/ai/assistant/stream", {
    method: "POST",
    body,
    signal,
  });
  if (!response.body) throw new Error("Assistant stream did not open.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushFrames = (): void => {
    let sep = frameSeparator(buffer);
    while (sep !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep).replace(/^(\r?\n){1,2}/, "");
      const frame = parseSseFrame(raw);
      if (frame) onFrame(frame);
      sep = frameSeparator(buffer);
    }
  };

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flushFrames();
    }
    buffer += decoder.decode();
    const tail = parseSseFrame(buffer);
    if (tail) onFrame(tail);
  } finally {
    reader.releaseLock();
  }
}

function frameSeparator(value: string): number {
  const lf = value.indexOf("\n\n");
  const crlf = value.indexOf("\r\n\r\n");
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

function parseSseFrame(raw: string): AiStreamFrame | null {
  const data = raw
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");
  if (!data) return null;
  return JSON.parse(data) as AiStreamFrame;
}

export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  steps: AssistantStep[] | null;
  createdAt: string;
}

export interface ConversationDetail extends ConversationSummary {
  messages: ConversationMessage[];
}

export function useConversations() {
  return useQuery({
    queryKey: ["ai-conversations"],
    queryFn: () =>
      api<{ conversations: ConversationSummary[] }>("/ai/conversations").then((r) => r.conversations),
  });
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: ["ai-conversation", id],
    enabled: Boolean(id),
    queryFn: () =>
      api<{ conversation: ConversationDetail }>(`/ai/conversations/${id}`).then((r) => r.conversation),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/ai/conversations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-conversations"] });
      void qc.invalidateQueries({ queryKey: ["ai-conversation"] });
    },
  });
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api<{ conversation: ConversationSummary }>(`/ai/conversations/${id}`, {
        method: "PATCH",
        body: { title },
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["ai-conversations"] });
      void qc.invalidateQueries({ queryKey: ["ai-conversation", data.conversation.id] });
    },
  });
}

export function useClearConversations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ deletedCount: number }>("/ai/conversations", {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-conversations"] });
      void qc.invalidateQueries({ queryKey: ["ai-conversation"] });
    },
  });
}

// ─── Witness inbox ───────────────────────────────────────────────────────────

export interface WitnessTask {
  id: string;
  status: "pending" | "confirmed" | "declined";
  respondedAt: string | null;
  createdAt: string;
  loanId: string | null;
  amount: number;
  currency: string;
  description: string | null;
  dueDate: string | null;
  loanStatus: string | null;
  accepted: boolean;
  inviterName: string;
  lenderName: string | null;
  borrowerName: string | null;
  actionable: boolean;
}

export function useWitnessTasks() {
  return useQuery({
    queryKey: ["witness-tasks"],
    queryFn: () => api<{ tasks: WitnessTask[] }>("/witness/tasks").then((r) => r.tasks),
    refetchInterval: 15000,
  });
}

/** Count of witness requests that need the user to act now (nav badge). */
export function useWitnessActionableCount(): number {
  const { data } = useWitnessTasks();
  return (data ?? []).filter((t) => t.actionable).length;
}

export function useRespondWitness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: "confirm" | "decline" }) =>
      api(`/witness/tasks/${vars.id}/respond`, { method: "POST", body: { action: vars.action } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["witness-tasks"] });
      void qc.invalidateQueries({ queryKey: ["loans"] });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useCreateTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      senderAccountId: string;
      receiverAccountId: string;
      amountMinor: number;
      currency: string;
      description?: string;
    }) => api("/transactions/transfer", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

// ─── Connections ─────────────────────────────────────────────────────────────

export interface PublicPerson {
  id: string;
  name: string;
  username: string | null;
}

export interface ConnectionsData {
  connections: Array<{ id: string; user: PublicPerson; since: string | null }>;
  incoming: Array<{ id: string; user: PublicPerson; createdAt: string }>;
  outgoing: Array<{ id: string; user: PublicPerson; createdAt: string }>;
}

export function useConnections() {
  return useQuery({
    queryKey: ["connections"],
    queryFn: () => api<ConnectionsData>("/connections"),
  });
}

export interface PersonSummary {
  person: PublicPerson;
  stats: {
    sharedTransactions: number;
    sharedLoans: number;
    activeLoans: number;
    witnessed: number;
    confirmedWitnesses: number;
  };
  loans: Array<{
    id: string;
    role: "lender" | "borrower";
    principal: number;
    outstanding: number;
    currency: string;
    status: string;
    description: string | null;
  }>;
}

export function usePersonSummary(id: string) {
  return useQuery({
    queryKey: ["person-summary", id],
    queryFn: () => api<PersonSummary>(`/people/${id}/summary`),
  });
}

export function useSearchUsers(q: string) {
  return useQuery({
    queryKey: ["user-search", q],
    enabled: q.trim().length >= 1,
    queryFn: () =>
      api<{ users: PublicPerson[] }>(`/users/search?q=${encodeURIComponent(q.trim())}`).then(
        (r) => r.users,
      ),
  });
}

export function useRequestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) =>
      api("/connections", { method: "POST", body: { username } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export function useRespondConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; accept: boolean }) =>
      api(`/connections/${vars.id}/respond`, { method: "POST", body: { accept: vars.accept } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export function useRemoveConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/connections/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export interface VisibleAccount {
  id: string;
  name: string;
  type: string;
  currency: string;
  visibility: AccountVisibility;
}

export function useUserAccounts(username: string | null) {
  return useQuery({
    queryKey: ["user-accounts", username],
    enabled: Boolean(username),
    queryFn: () =>
      api<{ user: PublicPerson; connected: boolean; accounts: VisibleAccount[] }>(
        `/users/${username}/accounts`,
      ),
  });
}

// ─── Profile ───────────────────────────────────────────────────────────────

export function useUpdateProfile() {
  return useMutation({
    mutationFn: (body: { name?: string; defaultCurrency?: string }) =>
      api("/auth/me", { method: "PATCH", body }),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: CategoryKind; name: string; icon?: string; color?: string }) =>
      api("/categories", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/categories/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (currentPassword: string) =>
      api("/auth/change-password", { method: "POST", body: { currentPassword } }),
  });
}

export interface McpTokenSummary {
  id: string;
  name: string;
  accessLevel: "view" | "admin";
  scopes: string[];
  prefix: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function useMcpTokens() {
  return useQuery({
    queryKey: ["mcp-tokens"],
    queryFn: () => api<{ tokens: McpTokenSummary[]; scopes: string[] }>("/mcp/tokens"),
  });
}

export function useCreateMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; accessLevel: "view" | "admin"; scopes: string[]; expiresInDays: number }) =>
      api<{ token: McpTokenSummary; secret: string }>("/mcp/tokens", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-tokens"] }),
  });
}

export function useRevokeMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/mcp/tokens/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-tokens"] }),
  });
}
